import type { HTMLAttributes } from 'react'

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded'
  width?: string | number
  height?: string | number
}

export function Skeleton({
  variant = 'rounded',
  width,
  height,
  className = '',
  style,
  ...props
}: SkeletonProps) {
  const variantClasses = {
    text: 'rounded-md h-4 w-full',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-xl',
  }

  return (
    <div
      className={`animate-pulse bg-[#E2E8F0]/70 ${variantClasses[variant]} ${className}`}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      {...props}
    />
  )
}

/**
 * Dashboard-Style Card Skeleton
 */
export function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`h-32 rounded-xl border border-slate-200/80 bg-white p-5 shadow-sm space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Skeleton variant="text" className="w-1/3 h-4" />
        <Skeleton variant="circular" className="h-8 w-8 shrink-0" />
      </div>
      <Skeleton variant="text" className="w-1/2 h-7" />
      <Skeleton variant="text" className="w-3/4 h-3" />
    </div>
  )
}

/**
 * Dashboard-Style Table Skeleton
 */
export function SkeletonTable({
  rows = 6,
  cols = 5,
  className = '',
}: {
  rows?: number
  cols?: number
  className?: string
}) {
  return (
    <div className={`w-full rounded-xl border border-slate-200/80 bg-white p-6 space-y-4 shadow-sm ${className}`}>
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <Skeleton variant="text" className="w-48 h-6" />
        <Skeleton variant="text" className="w-24 h-8 rounded-lg" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, rIdx) => (
          <div key={`skel-row-${rIdx}`} className="grid items-center gap-4 py-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {Array.from({ length: cols }).map((_, cIdx) => (
              <Skeleton key={`skel-cell-${rIdx}-${cIdx}`} variant="text" className="h-5 w-full" />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Dashboard-Style List Item Skeleton
 */
export function SkeletonList({ count = 4, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`skel-list-${idx}`} className="flex items-center gap-3.5 p-3 rounded-lg border border-slate-100 bg-slate-50/50">
          <Skeleton variant="circular" className="h-10 w-10 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" className="w-1/3 h-4" />
            <Skeleton variant="text" className="w-2/3 h-3" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Universal Dashboard-Style Screen Skeleton Fallback
 * Used for all page loading transitions matching the School Admin Dashboard skeleton.
 */
export function PageSkeleton() {
  return (
    <div className="min-h-screen text-[#111827] bg-[#F8FAFC] font-['Inter',sans-serif] animate-pulse">
      <div className="w-full max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Page Header Skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <div className="h-7 w-64 bg-slate-200 rounded-md" />
            <div className="h-4 w-96 bg-slate-200 rounded-md" />
          </div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-28 bg-slate-200 rounded-lg" />
            <div className="h-10 w-28 bg-slate-200 rounded-lg" />
          </div>
        </div>

        {/* 4-Card KPI Stat Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-white rounded-xl border border-slate-200/80 p-5 space-y-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="h-4 w-24 bg-slate-200 rounded" />
                <div className="h-8 w-8 bg-slate-200 rounded-lg" />
              </div>
              <div className="h-7 w-28 bg-slate-200 rounded" />
              <div className="h-3 w-36 bg-slate-200 rounded" />
            </div>
          ))}
        </div>

        {/* Data Table / Content Section Skeleton */}
        <div className="w-full rounded-xl border border-slate-200/80 bg-white p-6 space-y-4 shadow-sm">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="h-6 w-48 bg-slate-200 rounded" />
            <div className="h-8 w-24 bg-slate-200 rounded-lg" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map((rIdx) => (
              <div key={rIdx} className="grid grid-cols-5 items-center gap-4 py-2">
                <div className="h-5 bg-slate-200 rounded w-full" />
                <div className="h-5 bg-slate-200 rounded w-full" />
                <div className="h-5 bg-slate-200 rounded w-full" />
                <div className="h-5 bg-slate-200 rounded w-full" />
                <div className="h-5 bg-slate-200 rounded w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
